export default function EsteroTravelTimingSection() {
  return (
    <section id="travel-timing" className="py-16 md:py-20 bg-[#F8F9FB]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <span className="inline-block text-[#B22222] text-xs font-bold uppercase tracking-[0.15em] mb-3">
            Travel Timing Intelligence
          </span>
          <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#1A365D] mb-4">
            When Estero Residents Need Passports Most
          </h2>
          <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            Understanding the travel rhythms of Southwest Florida helps you plan ahead — or recognize when you need emergency service.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {/* Card 1 */}
          <div className="bg-white rounded-lg p-6 md:p-8 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center shrink-0">
                <i className="ri-flight-takeoff-line text-[#F6C344] text-lg"></i>
              </div>
              <h3 className="font-serif text-lg font-bold text-[#1A365D]">
                RSW — Southwest Florida International
              </h3>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              RSW sits just 15–20 minutes north of Estero and serves as the primary airport for Lee and Collier counties. The airport offers growing seasonal international service to Toronto, Montreal, London, and Frankfurt, plus year-round Caribbean and domestic routes. FGCU faculty and students frequently fly through RSW for academic conferences and study abroad programs, while retirees use it for European river cruises and Canadian summer visits.
            </p>
            <div className="bg-[#F8F9FB] rounded-md p-3 text-xs text-gray-500">
              <span className="font-semibold text-[#1A365D]">Tip:</span> International routes at RSW expand significantly November–April — book passport renewals before snowbird season begins.
            </div>
          </div>

          {/* Card 2 */}
          <div className="bg-white rounded-lg p-6 md:p-8 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center shrink-0">
                <i className="ri-ship-line text-[#F6C344] text-lg"></i>
              </div>
              <h3 className="font-serif text-lg font-bold text-[#1A365D]">
                Cruise Departures from Tampa, Fort Lauderdale &amp; Miami
              </h3>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              Estero residents frequently cruise from Port Tampa Bay (~2 hours north) for western Caribbean itineraries, Port Everglades in Fort Lauderdale (~2.25 hours southeast), or Port of Miami (~2.5 hours southeast) for eastern and southern Caribbean routes. The cruise season peaks November through April, aligning perfectly with Southwest Florida's snowbird population. Many retirees book cruises 6–12 months in advance but forget to check passport validity until the cruise documents arrive.
            </p>
            <div className="bg-[#F8F9FB] rounded-md p-3 text-xs text-gray-500">
              <span className="font-semibold text-[#1A365D]">Tip:</span> Some cruises require 6 months validity beyond the return date — verify your expiration date before final payment.
            </div>
          </div>

          {/* Card 3 */}
          <div className="bg-white rounded-lg p-6 md:p-8 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center shrink-0">
                <i className="ri-sun-line text-[#F6C344] text-lg"></i>
              </div>
              <h3 className="font-serif text-lg font-bold text-[#1A365D]">
                Snowbirds, Seasonal Migration &amp; Reverse Travel
              </h3>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              Estero's retiree community follows the classic Southwest Florida snowbird pattern: arriving in November and departing in April or May. This creates a predictable dormant passport trap — the passport is used once in the fall, then forgotten until the next international trip is booked. The September–October discovery window is our busiest period, as snowbirds realize their passports expired during the summer dormancy. Young families face a parallel trap when children who received passports at age 5 or 6 turn 10 or 11 and need renewal before that first big family trip.
            </p>
            <div className="bg-[#F8F9FB] rounded-md p-3 text-xs text-gray-500">
              <span className="font-semibold text-[#1A365D]">Tip:</span> Set a calendar reminder for September 1st to check every family member's passport expiration date before booking winter travel.
            </div>
          </div>

          {/* Card 4 */}
          <div className="bg-white rounded-lg p-6 md:p-8 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center shrink-0">
                <i className="ri-graduation-cap-line text-[#F6C344] text-lg"></i>
              </div>
              <h3 className="font-serif text-lg font-bold text-[#1A365D]">
                FGCU Academic Travel &amp; Study Abroad
              </h3>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              Florida Gulf Coast University generates significant international travel demand from its faculty, staff, and student population. Study abroad programs run during winter break, spring semester, and summer sessions — often with application deadlines that require valid passports months before departure. Faculty research collaborations, conference presentations, and professional development trips to Europe, Asia, and Latin America operate on academic calendars that do not wait for routine passport processing. The five-year child passport cycle also affects students who first traveled internationally as minors and now need adult passports for continued study abroad.
            </p>
            <div className="bg-[#F8F9FB] rounded-md p-3 text-xs text-gray-500">
              <span className="font-semibold text-[#1A365D]">Tip:</span> Check your study abroad or conference registration deadlines — most require a valid passport number 60–90 days before departure.
            </div>
          </div>
        </div>

        {/* Warning callout */}
        <div className="mt-10 bg-[#FFF8E7] border-l-4 border-[#F6C344] rounded-r-lg p-6 flex items-start gap-4">
          <i className="ri-alarm-warning-line text-[#F6C344] text-xl shrink-0 mt-0.5"></i>
          <div>
            <h4 className="font-semibold text-[#1A365D] mb-1">Snowbird &amp; Academic Season Surge</h4>
            <p className="text-gray-600 text-sm leading-relaxed">
              November through April is peak season for both snowbird travel and FGCU study abroad programs. Passport processing times lengthen during this period nationwide. We recommend starting renewals by September for winter travel and by February for summer programs. Emergency service is always available, but planning ahead eliminates stress.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}